import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { toast } from 'react-toastify';

import { DropDownOptions } from 'devextreme-react/autocomplete';
import Form, { Item, RequiredRule } from 'devextreme-react/form';
import ValidationGroup from 'devextreme-react/validation-group';
import Validator from 'devextreme-react/validator';

import { AppCol, AppFieldBox, AppLayoutTop } from 'components/appComponents';
import ClaimsProvider from 'components/claimsBlock';
import { MONEY_FORMAT } from 'constants/formats';
import { Button, DateBox, NumberBox, SelectBox, TextArea } from 'devextreme-react';
import $createDataSource from 'utils/create-data-souce-instance';

import {
  useChangeCashFlowExpenseFareMutation,
  useCreateCashFlowExpenseFareMutation,
  useGetCashFlowAccountsQuery,
  useLazyGetCashFlowExpenseFareQuery,
} from 'bus/cash';

import { CashFlowExpenseFareDetail } from './components';
import { initValue } from './utils';

const CashFlowExpenseFare: FC = () => {
  const history = useHistory();
  let { id } = useParams<{ id: string }>();

  const [form, setForm] = useState(initValue);
  const createValidationRef = useRef<ValidationGroup>(null);

  const [lazyGetCashFlowExpenseFare] = useLazyGetCashFlowExpenseFareQuery();
  const [changeCashFlowExpenseFare, { isLoading: isLoadingChange }] = useChangeCashFlowExpenseFareMutation();
  const [createCashFlowExpenseFare, { isLoading: isLoadingCreate }] = useCreateCashFlowExpenseFareMutation();
  const { data: cashFlowAccounts } = useGetCashFlowAccountsQuery();
  const disabledSubmit = isLoadingChange || isLoadingCreate;

  const handleSubmit = async (e: any) => {
    e.preventDefault && e.preventDefault();

    if (createValidationRef.current?.instance.validate().isValid) {
      if (id) {
        await changeCashFlowExpenseFare({ id, ...form });
        toast.success('Изменения успешно сохранены', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } else {
        await createCashFlowExpenseFare(form);
        history.push('/cash-flow');
      }
    }
  };

  const requestDateSource = useMemo(() => {
    const defaultFilter = ['cashFlowType', '=', '1'];
    return $createDataSource('/v1/CashFlowItem', 'id', { defaultFilter });
  }, []);

  const getInit = useCallback(async () => {
    if (id) {
      const result = await lazyGetCashFlowExpenseFare({ id });
      result && setForm(result.data!);
    }
  }, [id, lazyGetCashFlowExpenseFare]);

  useEffect(() => {
    getInit();
  }, [getInit]);

  return (
    <div className="mt-5">
      <div style={{ width: 750 }}>
        <form onSubmit={handleSubmit}>
          <ValidationGroup ref={createValidationRef}>
            <AppLayoutTop title="Расходы на запчасти">
              <ClaimsProvider claimsBlock={['CanEditExpenseFare']}>
                <Button
                  text="Сохранить"
                  type="default"
                  useSubmitBehavior={false}
                  onClick={handleSubmit}
                  disabled={disabledSubmit}
                />
              </ClaimsProvider>
            </AppLayoutTop>
            <div className="dx-card">
              <Form formData={form}>
                <Item colSpan={12} colCount={12} itemType="group">
                  <AppCol col={4}>
                    <AppFieldBox label="Дата" labelTop>
                      <DateBox
                        stylingMode="underlined"
                        type="date"
                        onValueChange={(e) => setForm((prev) => ({ ...prev, date: e as string }))}
                        useMaskBehavior
                        dateSerializationFormat="yyyy-MM-dd"
                        value={form.date}
                      >
                        <Validator>
                          <RequiredRule message="Заполните поле Дата" />
                        </Validator>
                      </DateBox>
                    </AppFieldBox>
                  </AppCol>

                  <AppCol col={4}>
                    <AppFieldBox label="Статья движения ДС" labelTop>
                      <SelectBox
                        stylingMode="underlined"
                        dataSource={requestDateSource}
                        displayExpr="name"
                        valueExpr="id"
                        value={form.cashFlowItemId}
                        searchEnabled
                        onItemClick={(e) => {
                          setForm((prev) => ({ ...prev, cashFlowItemId: e.itemData.id }));
                        }}
                      >
                        <DropDownOptions
                          title="Выберите Статья движения ДС"
                          closeOnOutsideClick={true}
                          showTitle={false}
                        />
                        <Validator>
                          <RequiredRule message="Выберите Статья движения ДС" />
                        </Validator>
                      </SelectBox>
                    </AppFieldBox>
                  </AppCol>

                  <AppCol col={4}>
                    <AppFieldBox label="Статья списания" labelTop>
                      <SelectBox
                        stylingMode="underlined"
                        items={cashFlowAccounts}
                        displayExpr="name"
                        valueExpr="id"
                        value={form.cashFlowAccount}
                        searchEnabled
                        onItemClick={(e) => {
                          setForm((prev) => ({ ...prev, cashFlowAccount: e.itemData.id }));
                        }}
                      >
                        <DropDownOptions
                          title="Выберите Статья списания"
                          closeOnOutsideClick={true}
                          showTitle={false}
                        />
                        <Validator>
                          <RequiredRule message="Выберите Статья списания" />
                        </Validator>
                      </SelectBox>
                    </AppFieldBox>
                  </AppCol>

                  {id && (
                    <AppCol col={4}>
                      <AppFieldBox label="Сумма" labelTop>
                        <NumberBox
                          stylingMode="underlined"
                          placeholder="0"
                          format={MONEY_FORMAT}
                          value={form.sum}
                        ></NumberBox>
                      </AppFieldBox>
                    </AppCol>
                  )}

                  <AppCol col={12}>
                    <AppFieldBox label="Комментарий" labelTop>
                      <TextArea
                        stylingMode="underlined"
                        placeholder="Введите комментарий"
                        mode="text"
                        value={form.comment || ''}
                        onChange={(e: any) => {
                          setForm((prev) => ({ ...prev, comment: e.event.currentTarget.value }));
                        }}
                      />
                    </AppFieldBox>
                  </AppCol>
                </Item>
              </Form>
            </div>
          </ValidationGroup>
        </form>
      </div>
      <ClaimsProvider claimsBlock={['CanLookExpenseFareDetails']}>
        {id && <CashFlowExpenseFareDetail cashFlowId={id} refreshForm={getInit} />}
      </ClaimsProvider>
    </div>
  );
};

export default CashFlowExpenseFare;
